import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, firestore } from '../lib/firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Roles: admin, accountant (księgowa), project_manager (kierownik projektu)
const ROLE_PERMISSIONS = {
  admin: {
    pages: ['/investments', '/', '/customers', '/payments', '/invoices', '/change-requests'],
    canEdit: true,
    defaultPage: '/investments',
  },
  accountant: {
    pages: ['/invoices'],
    canEdit: false,
  },
  project_manager: {
    pages: ['/investments', '/', '/customers', '/payments'],
    canEdit: false,
    // '/customers' - kierownicy mają wgrywać umowy (PDF); to jedyna rzecz na tej
    // stronie schowana za canEdit, reszta akcji i tak jest dla nich widoczna.
    canEditPages: ['/payments', '/customers'],
    defaultPage: '/investments',
  },
};

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('admin'); // default admin for existing users
  const [userInvestmentId, setUserInvestmentId] = useState(null); // for PM: restricted to this investment
  const [loading, setLoading] = useState(true);

  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  async function login(email, password) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const roleDoc = await getDoc(doc(firestore, 'user_roles', cred.user.uid));
    const role = roleDoc.exists() ? (roleDoc.data().role || 'admin') : 'admin';
    return { user: cred.user, role };
  }

  function logout() {
    return signOut(auth);
  }

  // Check if user can access a page
  function canAccess(path) {
    const perms = ROLE_PERMISSIONS[userRole];
    if (!perms) return true; // unknown role = full access (safe default for existing users)
    return perms.pages.includes(path);
  }

  // Check if user can edit on current page
  function canEdit(path) {
    if (userRole === 'admin') return true;
    const perms = ROLE_PERMISSIONS[userRole];
    if (!perms) return true;
    if (perms.canEdit) return true;
    if (perms.canEditPages && perms.canEditPages.includes(path)) return true;
    return false;
  }

  function isAdmin() {
    return userRole === 'admin';
  }

  function isProjectManager() {
    return userRole === 'project_manager';
  }

  function isAccountant() {
    return userRole === 'accountant';
  }

  function getDefaultPage(role) {
    const r = role || userRole;
    const perms = ROLE_PERMISSIONS[r];
    return perms?.defaultPage || perms?.pages?.[0] || '/';
  }

  // Listen for auth state changes and fetch role
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Fetch user role from Firestore
        try {
          const roleDoc = await getDoc(doc(firestore, 'user_roles', user.uid));
          if (roleDoc.exists()) {
            const data = roleDoc.data();
            setUserRole(data.role || 'admin');
            setUserInvestmentId(data.investmentId || null);
          } else {
            // No role doc = admin (existing users)
            setUserRole('admin');
            setUserInvestmentId(null);
          }
        } catch (e) {
          console.error('Error fetching user role:', e);
          setUserRole('admin');
        }
      } else {
        setUserRole('admin');
        setUserInvestmentId(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAuth: !!currentUser,
    userRole,
    userInvestmentId,
    signup,
    login,
    logout,
    canAccess,
    canEdit,
    isAdmin,
    isProjectManager,
    isAccountant,
    getDefaultPage,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
